import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { markAppControlledReload } from 'src/utils/appReloadSignal';

const EVENT_PIXEL_ROUTE_CONTEXT_KEY = '__uticket_event_pixel_route_context__';
const EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY =
    '__uticket_event_pixel_route_reload_flag__';

const getEventIdFromPathname = (pathname: string) => {
    const match = pathname.match(/^\/evento\/[^/]+\/([^/]+)/);
    return match?.[1] ?? null;
};

const getRouteContext = (pathname: string) => {
    const eventId = getEventIdFromPathname(pathname);

    return eventId ? `event:${eventId}` : 'non-event';
};

const isEventContext = (context: string | null) => {
    return !!context && context.startsWith('event:');
};

const getReloadKey = (context: string, pathname: string, search: string) => {
    return `${context}:${pathname}${search}`;
};

const shouldReloadForContextChange = (
    previousContext: string | null,
    currentContext: string
) => {
    if (!previousContext) return false;
    if (previousContext === currentContext) return false;

    return isEventContext(previousContext);
};

const syncCurrentRouteContext = (currentContext: string) => {
    sessionStorage.setItem(EVENT_PIXEL_ROUTE_CONTEXT_KEY, currentContext);
    sessionStorage.removeItem(EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY);
};

const forceCleanReloadForContext = (
    currentContext: string,
    pathname: string,
    search: string
) => {
    sessionStorage.setItem(EVENT_PIXEL_ROUTE_CONTEXT_KEY, currentContext);
    sessionStorage.setItem(
        EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY,
        getReloadKey(currentContext, pathname, search)
    );

    markAppControlledReload();
    window.location.replace(window.location.href);
};

export const EventPixelNavigationGuard = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const hasMountedRef = React.useRef(false);

    React.useEffect(() => {
        const currentContext = getRouteContext(location.pathname);
        const previousContext = sessionStorage.getItem(
            EVENT_PIXEL_ROUTE_CONTEXT_KEY
        );

        const reloadKey = getReloadKey(
            currentContext,
            location.pathname,
            location.search
        );

        const reloadFlag = sessionStorage.getItem(
            EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY
        );

        if (reloadFlag === reloadKey) {
            syncCurrentRouteContext(currentContext);
            hasMountedRef.current = true;
            return;
        }

        if (!hasMountedRef.current) {
            syncCurrentRouteContext(currentContext);
            hasMountedRef.current = true;
            return;
        }

        const isBrowserBackForward = navigationType === 'POP';
        const hasChangedContext = previousContext !== currentContext;

        if (
            isBrowserBackForward &&
            hasChangedContext &&
            shouldReloadForContextChange(previousContext, currentContext)
        ) {
            forceCleanReloadForContext(
                currentContext,
                location.pathname,
                location.search
            );
            return;
        }

        if (shouldReloadForContextChange(previousContext, currentContext)) {
            forceCleanReloadForContext(
                currentContext,
                location.pathname,
                location.search
            );
            return;
        }

        syncCurrentRouteContext(currentContext);
    }, [location.pathname, location.search, navigationType]);

    React.useEffect(() => {
        const handlePageShow = (pageShowEvent: PageTransitionEvent) => {
            const navigationEntries = window.performance.getEntriesByType(
                'navigation'
            ) as PerformanceNavigationTiming[];

            const isBackForwardNavigation =
                navigationEntries?.[0]?.type === 'back_forward';

            if (!pageShowEvent.persisted && !isBackForwardNavigation) {
                return;
            }

            const currentContext = getRouteContext(window.location.pathname);
            const previousContext = sessionStorage.getItem(
                EVENT_PIXEL_ROUTE_CONTEXT_KEY
            );

            const reloadKey = getReloadKey(
                currentContext,
                window.location.pathname,
                window.location.search
            );

            const reloadFlag = sessionStorage.getItem(
                EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY
            );

            if (reloadFlag === reloadKey) {
                syncCurrentRouteContext(currentContext);
                return;
            }

            if (shouldReloadForContextChange(previousContext, currentContext)) {
                sessionStorage.setItem(
                    EVENT_PIXEL_ROUTE_RELOAD_FLAG_KEY,
                    reloadKey
                );

                markAppControlledReload();
                window.location.reload();
                return;
            }

            syncCurrentRouteContext(currentContext);
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []);

    return null;
};